import styled, { DefaultTheme } from 'styled-components';
import { Input } from 'antd';
import { StatusDotProps, OptionItemProps } from './types';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

const ACCENT = '#2d6cca';
const ACCENT_RGB = '45, 108, 202';

const statusColor = (status: boolean | null) =>
  status === true ? '#16a34a' : status === false ? '#ef4444' : 'transparent';

export const Container = styled.div<{ theme?: DefaultTheme }>`
  flex: 1;
  min-width: 260px;
`;

export const TriggerCard = styled.button<{ theme?: DefaultTheme }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 16px;
  cursor: pointer;
  border: 1px solid ${(props) => getThemeValue(props, `rgba(${ACCENT_RGB}, 0.35)`, `rgba(${ACCENT_RGB}, 0.25)`)};
  background-color: ${(props) => getThemeValue(props, '#1e293b', '#ffffff')};
  box-shadow: ${(props) =>
    getThemeValue(props, '0 1px 2px rgba(0, 0, 0, 0.3)', '0 1px 2px rgba(15, 23, 42, 0.04)')};
  transition: all 0.15s ease;

  &:hover {
    border-color: ${ACCENT};
    box-shadow: 0 0 0 3px rgba(${ACCENT_RGB}, 0.12);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const StoreAvatar = styled.span<StatusDotProps & { theme?: DefaultTheme }>`
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: ${(props) => getThemeValue(props, `rgba(${ACCENT_RGB}, 0.2)`, `rgba(${ACCENT_RGB}, 0.1)`)};
  color: ${ACCENT};
  font-size: 15px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: inherit;
  }

  &::after {
    content: '';
    position: absolute;
    right: -2px;
    bottom: -2px;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background-color: ${(props) => statusColor(props.status)};
    border: 2px solid ${(props) => getThemeValue(props, '#1e293b', '#ffffff')};
    display: ${(props) => (props.status === null ? 'none' : 'block')};
  }
`;

export const TriggerBody = styled.span`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  text-align: left;
`;

export const TriggerName = styled.span<{ theme?: DefaultTheme }>`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
  color: ${(props) => getThemeValue(props, '#f1f5f9', '#0f172a')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  svg {
    color: ${(props) => getThemeValue(props, '#94a3b8', '#94a3b8')};
    flex-shrink: 0;
  }
`;

export const TriggerMeta = styled.span<{ theme?: DefaultTheme }>`
  font-size: 12px;
  line-height: 1.3;
  color: ${(props) => getThemeValue(props, '#94a3b8', '#64748b')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DropdownContainer = styled.div<{ theme?: DefaultTheme }>`
  width: 420px;
  max-width: 92vw;
  background: ${(props) => getThemeValue(props, '#1e293b', '#ffffff')};
  border: 1px solid ${(props) => getThemeValue(props, '#334155', '#e2e8f0')};
  border-radius: 14px;
  box-shadow: ${(props) =>
    getThemeValue(props, '0 12px 24px rgba(0, 0, 0, 0.45)', '0 12px 24px rgba(15, 23, 42, 0.12)')};
  padding: 10px;
`;

export const SearchInput = styled(Input)`
  margin-bottom: 8px;
  border-radius: 8px !important;
  padding: 6px 10px !important;
`;

export const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 320px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
`;

export const OptionItem = styled.button<OptionItemProps & { theme?: DefaultTheme }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 10px 8px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  background-color: ${(props) =>
    props.isSelected ? getThemeValue(props, `rgba(${ACCENT_RGB}, 0.18)`, `rgba(${ACCENT_RGB}, 0.08)`) : 'transparent'};
  transition: background-color 0.15s ease;

  & + & {
    margin-top: 2px;
    border-top: 1px solid ${(props) => getThemeValue(props, '#334155', '#f1f5f9')};
    border-radius: 0;
    padding-top: 12px;
  }

  &:hover {
    background-color: ${(props) =>
      props.isSelected
        ? getThemeValue(props, `rgba(${ACCENT_RGB}, 0.24)`, `rgba(${ACCENT_RGB}, 0.12)`)
        : getThemeValue(props, 'rgba(148, 163, 184, 0.12)', 'rgba(15, 23, 42, 0.04)')};
  }
`;

export const OptionMain = styled.span`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  overflow: hidden;
  min-width: 0;
`;

export const OptionTextGroup = styled.span`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
`;

export const OptionName = styled.span<{ theme?: DefaultTheme }>`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  color: ${(props) => getThemeValue(props, '#f1f5f9', '#0f172a')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const OptionAddress = styled.span<{ theme?: DefaultTheme }>`
  font-size: 12px;
  line-height: 1.3;
  color: ${(props) => getThemeValue(props, '#94a3b8', '#64748b')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const OptionSide = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

export const StatusBadge = styled.span<StatusDotProps>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  color: ${(props) => (props.status ? '#16a34a' : '#ef4444')};
  background-color: ${(props) => (props.status ? 'rgba(22, 163, 74, 0.12)' : 'rgba(239, 68, 68, 0.12)')};
`;

export const EmptyState = styled.div<{ theme?: DefaultTheme }>`
  padding: 20px 12px;
  text-align: center;
  font-size: 13px;
  color: ${(props) => getThemeValue(props, '#94a3b8', '#64748b')};
`;

export const LoadingContainer = styled.div`
  padding: 20px;
  text-align: center;
`;

export const Divider = styled.div<{ theme?: DefaultTheme }>`
  height: 1px;
  margin: 6px 4px;
  background-color: ${(props) => getThemeValue(props, '#334155', '#e5e7eb')};
`;

export const AddStoreRow = styled.button<{ theme?: DefaultTheme }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  background-color: transparent;

  &:hover {
    background-color: ${(props) => getThemeValue(props, 'rgba(148, 163, 184, 0.12)', 'rgba(15, 23, 42, 0.04)')};
  }
`;

export const AddStoreIcon = styled.span<{ theme?: DefaultTheme }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: ${(props) => getThemeValue(props, `rgba(${ACCENT_RGB}, 0.2)`, `rgba(${ACCENT_RGB}, 0.1)`)};
  color: ${ACCENT};
  font-size: 14px;
`;

export const AddStoreTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${ACCENT};
`;

export const AddStoreSubtitle = styled.span<{ theme?: DefaultTheme }>`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#94a3b8', '#64748b')};
`;
